import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Typography, Form, Input, InputNumber, Button, Card, Space, Alert, Radio, Select } from 'antd';
import { createSession } from '../../utils/sessionStorage';
import LeaderPicker from './LeaderPicker';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const { Title, Text } = Typography;

function isLeaderCard(card) {
  const cat = (card.category || '').toLowerCase();
  const types = (card.types || []).map((t) => (t || '').toLowerCase());
  return cat.includes('leader') || types.some((t) => t.includes('leader'));
}

export default function CreateSession() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [sessionOptions, setSessionOptions] = useState({ formats: [], session_types: [] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const leaderOptions = React.useMemo(() => {
    const leaders = cards.filter(isLeaderCard);
    if (leaders.length > 0) return leaders;
    return cards;
  }, [cards]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/cards/all-base-cards`)
      .then(({ data }) => setCards(Array.isArray(data) ? data : []))
      .catch(() => setCards([]))
      .finally(() => setLoadingCards(false));
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/session-options`)
      .then(({ data }) => setSessionOptions({
        formats: data.formats || [],
        session_types: data.session_types || [],
      }))
      .catch(() => setSessionOptions({ formats: [], session_types: [] }));
  }, []);

  const onFinish = (values) => {
    setError(null);
    setSubmitting(true);
    const leaderCard = leaderOptions.find(
      (c) => c.card_id === values.myLeaderCardId || `${c.card_id} ${c.name}` === values.myLeaderCardId
    );
    const myLeaderCardId = leaderCard?.card_id ?? values.myLeaderCardId;
    const myLeaderName = leaderCard?.name ?? values.myLeaderCardId ?? '';
    const myLeaderImageUrl = leaderCard?.img_full_url ?? null;
    const sessionType = values.sessionType === 'practice' ? 'practice' : 'tournament';
    try {
      const session = createSession({
        tournamentName: values.tournamentName?.trim() || 'Unnamed',
        myLeaderCardId,
        myLeaderName,
        myLeaderImageUrl,
        playerCount: sessionType === 'tournament' ? (values.playerCount ?? 0) : null,
        finalPlacement: sessionType === 'tournament' && values.finalPlacement != null && values.finalPlacement !== '' ? values.finalPlacement : null,
        sessionType,
        format: values.format || null,
      });
      navigate(`/session-tracker/${session.id}`);
    } catch (err) {
      const msg = err && typeof err === 'object' && typeof err.message === 'string'
        ? err.message
        : typeof err === 'string'
          ? err
          : 'Failed to create session';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isTournament = Form.useWatch('sessionType', form) !== 'practice';

  return (
    <div className="session-tracker-create">
      <div className="session-tracker-create-head">
        <Title level={4}>New session</Title>
        <Text type="secondary">Create a tournament or practice session to track your games.</Text>
      </div>

      {error && (
        <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} style={{ marginTop: 12 }} />
      )}

      <Card className="session-tracker-create-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ sessionType: 'tournament', playerCount: 32 }}
        >
          <Form.Item name="sessionType" label="Session type" rules={[{ required: true }]}>
            <Radio.Group>
              {sessionOptions.session_types.map((t) => (
                <Radio key={t.value} value={t.value}>{t.label}</Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="tournamentName"
            label="Session name"
            rules={[{ required: true, message: 'Enter a name' }]}
          >
            <Input placeholder={isTournament ? 'e.g. Store Championship Feb 2025' : 'e.g. Practice Feb 2025'} />
          </Form.Item>

          <Form.Item
            name="myLeaderCardId"
            label="Your leader"
            rules={[{ required: true, message: 'Choose your leader' }]}
          >
            <LeaderPicker
              options={leaderOptions}
              loading={loadingCards}
              placeholder="Choose your leader"
              label={null}
            />
          </Form.Item>

          <Form.Item name="format" label="Format">
            <Select
              allowClear
              placeholder="Select format"
              options={sessionOptions.formats.map((f) => ({ value: f, label: f }))}
            />
          </Form.Item>

          {isTournament && (
            <>
              <Form.Item
                name="playerCount"
                label="Number of players"
                rules={[{ required: true }]}
              >
                <InputNumber min={2} max={1000} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="finalPlacement" label="Your final placement (optional)">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Add when you finish the tournament" />
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Create session
              </Button>
              <Button onClick={() => navigate('/session-tracker')}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
